import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import useReveal from "../shared/useReveal";
import "./CalcTeaser.css";
import "./HomeSections.css";

const TABS = [
  { id: "sip", label: "SIP" },
  { id: "lumpsum", label: "Lumpsum" },
  { id: "retire", label: "Retirement" },
  { id: "emi", label: "EMI" },
  { id: "cagr", label: "CAGR" },
  { id: "goal", label: "Goal" },
  { id: "bond", label: "Bond Yield" },
];

function formatINR(n) {
  return "₹" + Math.round(n || 0).toLocaleString("en-IN");
}

function formatPct(n, digits = 2) {
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

function sipFV(monthly, annualRate, years) {
  const r = annualRate / 12 / 100;
  const n = years * 12;
  if (n <= 0) return 0;
  if (r === 0) return monthly * n;
  return monthly * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
}

function monthlyForGoal(FV, annualRate, years) {
  const r = annualRate / 12 / 100;
  const n = years * 12;
  if (n <= 0) return 0;
  if (r === 0) return FV / n;
  return FV / (((Math.pow(1 + r, n) - 1) / r) * (1 + r));
}

function Field({ label, valueLabel, min, max, step, value, onChange }) {
  return (
    <div className="home-field">
      <label>
        {label} <output>{valueLabel}</output>
      </label>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

function NumberField({ label, value, min, max, step, onChange }) {
  return (
    <div className="home-field">
      <label>{label}</label>
      <input
        className="home-field-number"
        type="number"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value) || 0)}
      />
    </div>
  );
}

function Results({ rows, chart }) {
  return (
    <div className="home-calc-results">
      {rows.map((row) => (
        <div
          className={`home-res-row${row.hero ? " hero-res" : ""}`}
          key={row.label}
        >
          <span>{row.label}</span>
          <b>{row.value}</b>
        </div>
      ))}
      {chart}
    </div>
  );
}

function compactINR(n) {
  const v = Math.abs(n);
  if (v >= 1e7) return `₹${(n / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(n / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(n / 1e3).toFixed(0)}K`;
  return formatINR(n);
}

function GrowthAnalyticsChart({
  points,
  years,
  gradientId = "homeGrowth",
  seriesLabel = "Wealth",
}) {
  const chart = useMemo(() => {
    const w = 360;
    const h = 168;
    const pad = { t: 18, r: 14, b: 28, l: 44 };
    const maxY = Math.max(...points.map((p) => p.wealth), 1);
    const maxX = Math.max(points.length - 1, 1);
    const x = (i) => pad.l + (i / maxX) * (w - pad.l - pad.r);
    const y = (v) => pad.t + (1 - v / maxY) * (h - pad.t - pad.b);

    const wealthLine = points
      .map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.wealth).toFixed(1)}`)
      .join(" ");
    const investedLine = points
      .map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.invested).toFixed(1)}`)
      .join(" ");
    const area = `${wealthLine} L${x(maxX).toFixed(1)},${y(0).toFixed(1)} L${x(0).toFixed(1)},${y(0).toFixed(1)} Z`;

    const grid = [0.25, 0.5, 0.75, 1].map((t) => ({
      y: y(maxY * t),
      label: compactINR(maxY * t),
    }));

    const xTicks = [0, Math.round(years / 2), years].filter(
      (v, i, arr) => arr.indexOf(v) === i
    );

    return {
      w,
      h,
      pad,
      x,
      y,
      wealthLine,
      investedLine,
      area,
      grid,
      xTicks,
      last: points[points.length - 1],
      points,
    };
  }, [points, years]);

  const step = Math.max(1, Math.ceil(years / 5));
  const areaId = `${gradientId}Area`;
  const lineId = `${gradientId}Line`;

  return (
    <div className="home-calc-chart" aria-hidden="true">
      <svg
        className="home-calc-chart-svg"
        viewBox={`0 0 ${chart.w} ${chart.h}`}
        role="img"
        aria-label="Wealth vs invested over years"
      >
        <defs>
          <linearGradient id={areaId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3fd68f" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#3fd68f" stopOpacity="0" />
          </linearGradient>
          <linearGradient id={lineId} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#3fd68f" />
            <stop offset="100%" stopColor="#E3A81C" />
          </linearGradient>
        </defs>

        {chart.grid.map((g) => (
          <g key={g.label}>
            <line
              x1={chart.pad.l}
              x2={chart.w - chart.pad.r}
              y1={g.y}
              y2={g.y}
              className="home-calc-grid"
            />
            <text
              x={chart.pad.l - 6}
              y={g.y + 3}
              textAnchor="end"
              className="home-calc-axis"
            >
              {g.label}
            </text>
          </g>
        ))}

        <path d={chart.area} fill={`url(#${areaId})`} />
        <path
          d={chart.investedLine}
          fill="none"
          stroke="rgba(232,240,236,0.45)"
          strokeWidth="1.75"
          strokeDasharray="4 3"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        <path
          d={chart.wealthLine}
          fill="none"
          stroke={`url(#${lineId})`}
          strokeWidth="2.4"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {chart.points
          .filter(
            (_, i) =>
              i === 0 || i === chart.points.length - 1 || i % step === 0
          )
          .map((p) => (
            <circle
              key={p.year}
              cx={chart.x(p.year)}
              cy={chart.y(p.wealth)}
              r={p.year === years ? 3.5 : 2.2}
              fill={p.year === years ? "#E3A81C" : "#3fd68f"}
              stroke="#101b16"
              strokeWidth="1.2"
            />
          ))}

        {chart.xTicks.map((yr) => (
          <text
            key={yr}
            x={chart.x(yr)}
            y={chart.h - 8}
            textAnchor={yr === 0 ? "start" : yr === years ? "end" : "middle"}
            className="home-calc-axis"
          >
            Yr {yr}
          </text>
        ))}
      </svg>
      <div className="home-calc-chart-legend">
        <span>
          <i className="home-calc-leg-wealth" /> {seriesLabel}
        </span>
        <span>
          <i className="home-calc-leg-invested" /> Invested
        </span>
        <span className="home-calc-chart-end">
          End {compactINR(chart.last?.wealth || 0)}
        </span>
      </div>
    </div>
  );
}

function SipPanel() {
  const [amt, setAmt] = useState(10000);
  const [ret, setRet] = useState(12);
  const [yrs, setYrs] = useState(15);

  const { invested, gain, fv, points } = useMemo(() => {
    const future = sipFV(amt, ret, yrs);
    const inv = amt * yrs * 12;
    const r = ret / 12 / 100;
    const series = [];
    for (let y = 0; y <= yrs; y++) {
      const n = y * 12;
      const investedY = amt * n;
      const wealth =
        y === 0
          ? 0
          : r === 0
            ? investedY
            : amt * ((Math.pow(1 + r, n) - 1) / r) * (1 + r);
      series.push({ year: y, invested: investedY, wealth });
    }
    return { invested: inv, gain: future - inv, fv: future, points: series };
  }, [amt, ret, yrs]);

  return (
    <div className="home-calc-grid">
      <div>
        <Field
          label="Monthly SIP"
          valueLabel={formatINR(amt)}
          min={500}
          max={200000}
          step={500}
          value={amt}
          onChange={setAmt}
        />
        <Field
          label="Expected Annual Return"
          valueLabel={`${ret}%`}
          min={4}
          max={24}
          step={0.5}
          value={ret}
          onChange={setRet}
        />
        <Field
          label="Time Period"
          valueLabel={`${yrs} years`}
          min={1}
          max={40}
          step={1}
          value={yrs}
          onChange={setYrs}
        />
        <Link to="/contact" className="btn btn-primary" style={{ width: "100%" }}>
          Start This SIP With an Expert
        </Link>
      </div>
      <Results
        rows={[
          { label: "Total Investment", value: formatINR(invested) },
          { label: "Estimated Returns", value: formatINR(gain) },
          { label: "Wealth Created", value: formatINR(fv), hero: true },
        ]}
        chart={
          <GrowthAnalyticsChart
            points={points}
            years={yrs}
            gradientId="homeSip"
          />
        }
      />
    </div>
  );
}

function LumpsumPanel() {
  const [amt, setAmt] = useState(500000);
  const [ret, setRet] = useState(12);
  const [yrs, setYrs] = useState(10);

  const { fv, gain, points } = useMemo(() => {
    const future = amt * Math.pow(1 + ret / 100, yrs);
    const series = [];
    for (let y = 0; y <= yrs; y++) {
      series.push({
        year: y,
        invested: amt,
        wealth: amt * Math.pow(1 + ret / 100, y),
      });
    }
    return { fv: future, gain: future - amt, points: series };
  }, [amt, ret, yrs]);

  return (
    <div className="home-calc-grid">
      <div>
        <Field
          label="One-time Investment"
          valueLabel={formatINR(amt)}
          min={10000}
          max={10000000}
          step={10000}
          value={amt}
          onChange={setAmt}
        />
        <Field
          label="Expected Annual Return"
          valueLabel={`${ret}%`}
          min={4}
          max={24}
          step={0.5}
          value={ret}
          onChange={setRet}
        />
        <Field
          label="Time Period"
          valueLabel={`${yrs} years`}
          min={1}
          max={40}
          step={1}
          value={yrs}
          onChange={setYrs}
        />
      </div>
      <Results
        rows={[
          { label: "Invested Amount", value: formatINR(amt) },
          { label: "Estimated Returns", value: formatINR(gain) },
          { label: "Maturity Value", value: formatINR(fv), hero: true },
        ]}
        chart={
          <GrowthAnalyticsChart
            points={points}
            years={yrs}
            gradientId="homeLump"
            seriesLabel="Maturity"
          />
        }
      />
    </div>
  );
}

function RetirementPanel() {
  const [age, setAge] = useState(30);
  const [retAge, setRetAge] = useState(60);
  const [exp, setExp] = useState(50000);
  const [inf, setInf] = useState(6);
  const [ret, setRet] = useState(12);

  const { expFut, corpus, sip, invested, gains, years } = useMemo(() => {
    const ra = Math.max(retAge, age + 1);
    const yrs = ra - age;
    const futureExp = exp * Math.pow(1 + inf / 100, yrs);
    const need = futureExp * 12 * 25;
    const monthly = monthlyForGoal(need, ret, yrs);
    const inv = monthly * yrs * 12;
    return {
      expFut: futureExp,
      corpus: need,
      sip: monthly,
      invested: inv,
      gains: Math.max(need - inv, 0),
      years: yrs,
    };
  }, [age, retAge, exp, inf, ret]);

  return (
    <div className="home-calc-grid">
      <div>
        <Field
          label="Current Age"
          valueLabel={String(age)}
          min={18}
          max={55}
          step={1}
          value={age}
          onChange={setAge}
        />
        <Field
          label="Retirement Age"
          valueLabel={String(retAge)}
          min={45}
          max={70}
          step={1}
          value={retAge}
          onChange={setRetAge}
        />
        <Field
          label="Monthly Expenses Today"
          valueLabel={formatINR(exp)}
          min={10000}
          max={500000}
          step={5000}
          value={exp}
          onChange={setExp}
        />
        <Field
          label="Inflation"
          valueLabel={`${inf}%`}
          min={3}
          max={10}
          step={0.5}
          value={inf}
          onChange={setInf}
        />
        <Field
          label="Expected Return"
          valueLabel={`${ret}%`}
          min={6}
          max={18}
          step={0.5}
          value={ret}
          onChange={setRet}
        />
      </div>
      <Results
        rows={[
          { label: "Monthly Expenses at Retirement", value: formatINR(expFut) },
          { label: "Retirement Corpus Needed", value: formatINR(corpus), hero: true },
          { label: "Monthly SIP Required", value: `${formatINR(sip)} /mo` },
        ]}
        chart={
          <RetirementPieChart
            invested={invested}
            gains={gains}
            corpus={corpus}
            years={years}
          />
        }
      />
    </div>
  );
}

function RetirementPieChart({ invested, gains, corpus, years }) {
  const slices = useMemo(() => {
    const total = Math.max(corpus, 1);
    const invPct = Math.min(100, (invested / total) * 100);
    const gainPct = Math.max(0, 100 - invPct);
    return [
      {
        key: "invested",
        label: "Your SIP total",
        value: invested,
        pct: invPct,
        color: "#0E7A5F",
      },
      {
        key: "gains",
        label: "Estimated returns",
        value: gains,
        pct: gainPct,
        color: "#E3A81C",
      },
    ];
  }, [invested, gains, corpus]);

  const arcs = useMemo(() => {
    const cx = 90;
    const cy = 90;
    const r = 68;
    const ir = 42;
    let angle = -Math.PI / 2;
    return slices.map((s) => {
      const sweep = (s.pct / 100) * Math.PI * 2;
      const a0 = angle;
      const a1 = angle + sweep;
      angle = a1;
      const large = sweep > Math.PI ? 1 : 0;
      const x0 = cx + r * Math.cos(a0);
      const y0 = cy + r * Math.sin(a0);
      const x1 = cx + r * Math.cos(a1);
      const y1 = cy + r * Math.sin(a1);
      const xi0 = cx + ir * Math.cos(a1);
      const yi0 = cy + ir * Math.sin(a1);
      const xi1 = cx + ir * Math.cos(a0);
      const yi1 = cy + ir * Math.sin(a0);
      const d = [
        `M ${x0} ${y0}`,
        `A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`,
        `L ${xi0} ${yi0}`,
        `A ${ir} ${ir} 0 ${large} 0 ${xi1} ${yi1}`,
        "Z",
      ].join(" ");
      return { ...s, d };
    });
  }, [slices]);

  return (
    <div className="home-calc-pie" aria-hidden="true">
      <svg viewBox="0 0 180 180" className="home-calc-pie-svg">
        {arcs.map((a) => (
          <path key={a.key} d={a.d} fill={a.color} />
        ))}
        <circle cx="90" cy="90" r="36" fill="#101b16" />
        <text x="90" y="86" textAnchor="middle" className="home-calc-pie-center-label">
          Corpus
        </text>
        <text x="90" y="104" textAnchor="middle" className="home-calc-pie-center-value">
          {compactINR(corpus)}
        </text>
      </svg>
      <div className="home-calc-pie-legend">
        {slices.map((s) => (
          <div key={s.key} className="home-calc-pie-row">
            <span className="home-calc-pie-swatch" style={{ background: s.color }} />
            <div>
              <strong>{s.label}</strong>
              <em>
                {compactINR(s.value)} · {s.pct.toFixed(0)}%
              </em>
            </div>
          </div>
        ))}
        <p className="home-calc-pie-note">
          Over {years} years of SIP to fund the 25× retirement corpus
        </p>
      </div>
    </div>
  );
}

function EmiPanel() {
  const [amt, setAmt] = useState(2500000);
  const [rate, setRate] = useState(9);
  const [yrs, setYrs] = useState(20);

  const { emi, interest, total } = useMemo(() => {
    const r = rate / 12 / 100;
    const n = yrs * 12;
    let e = 0;
    if (n <= 0) e = 0;
    else if (r === 0) e = amt / n;
    else e = (amt * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const tot = e * n;
    return { emi: e, total: tot, interest: tot - amt };
  }, [amt, rate, yrs]);

  return (
    <div className="home-calc-grid">
      <div>
        <Field
          label="Loan Amount"
          valueLabel={formatINR(amt)}
          min={100000}
          max={50000000}
          step={100000}
          value={amt}
          onChange={setAmt}
        />
        <Field
          label="Interest Rate (p.a.)"
          valueLabel={`${rate}%`}
          min={6}
          max={18}
          step={0.1}
          value={rate}
          onChange={setRate}
        />
        <Field
          label="Tenure"
          valueLabel={`${yrs} years`}
          min={1}
          max={30}
          step={1}
          value={yrs}
          onChange={setYrs}
        />
      </div>
      <Results
        rows={[
          { label: "Monthly EMI", value: formatINR(emi), hero: true },
          { label: "Total Interest Payable", value: formatINR(interest) },
          { label: "Total Payment", value: formatINR(total) },
        ]}
      />
    </div>
  );
}

function CagrPanel() {
  const [pv, setPv] = useState(100000);
  const [fv, setFv] = useState(250000);
  const [yrs, setYrs] = useState(5);

  const { cagr, abs } = useMemo(() => {
    if (pv <= 0 || fv <= 0 || yrs <= 0) return { cagr: NaN, abs: NaN };
    return {
      cagr: (Math.pow(fv / pv, 1 / yrs) - 1) * 100,
      abs: ((fv - pv) / pv) * 100,
    };
  }, [pv, fv, yrs]);

  return (
    <div className="home-calc-grid">
      <div>
        <NumberField
          label="Initial Value (₹)"
          value={pv}
          min={1}
          max={100000000}
          step={1000}
          onChange={setPv}
        />
        <NumberField
          label="Final Value (₹)"
          value={fv}
          min={1}
          max={100000000}
          step={1000}
          onChange={setFv}
        />
        <Field
          label="Years"
          valueLabel={String(yrs)}
          min={1}
          max={40}
          step={1}
          value={yrs}
          onChange={setYrs}
        />
      </div>
      <Results
        rows={[
          { label: "CAGR", value: formatPct(cagr), hero: true },
          { label: "Absolute Return", value: formatPct(abs, 1) },
        ]}
      />
    </div>
  );
}

function GoalPanel() {
  const [amt, setAmt] = useState(10000000);
  const [yrs, setYrs] = useState(15);
  const [ret, setRet] = useState(12);

  const { sip, lump } = useMemo(() => {
    const monthly = monthlyForGoal(amt, ret, yrs);
    const today = amt / Math.pow(1 + ret / 100, yrs);
    return { sip: monthly, lump: today };
  }, [amt, yrs, ret]);

  return (
    <div className="home-calc-grid">
      <div>
        <Field
          label="Goal Amount"
          valueLabel={formatINR(amt)}
          min={100000}
          max={100000000}
          step={100000}
          value={amt}
          onChange={setAmt}
        />
        <Field
          label="Years to Goal"
          valueLabel={String(yrs)}
          min={1}
          max={40}
          step={1}
          value={yrs}
          onChange={setYrs}
        />
        <Field
          label="Expected Return"
          valueLabel={`${ret}%`}
          min={4}
          max={20}
          step={0.5}
          value={ret}
          onChange={setRet}
        />
      </div>
      <Results
        rows={[
          { label: "Monthly SIP Required", value: `${formatINR(sip)} /mo`, hero: true },
          { label: "Or One-time Lumpsum Today", value: formatINR(lump) },
        ]}
      />
    </div>
  );
}

function BondPanel() {
  const [face, setFace] = useState(1000);
  const [price, setPrice] = useState(950);
  const [coupon, setCoupon] = useState(7);
  const [yrs, setYrs] = useState(5);

  const { cy, ytm, cpAmt } = useMemo(() => {
    if (face <= 0 || price <= 0 || yrs <= 0) {
      return { cy: NaN, ytm: NaN, cpAmt: 0 };
    }
    const cp = (face * coupon) / 100;
    return {
      cpAmt: cp,
      cy: (cp / price) * 100,
      ytm: ((cp + (face - price) / yrs) / ((face + price) / 2)) * 100,
    };
  }, [face, price, coupon, yrs]);

  return (
    <div className="home-calc-grid">
      <div>
        <NumberField
          label="Face Value (₹)"
          value={face}
          min={100}
          max={1000000}
          step={100}
          onChange={setFace}
        />
        <NumberField
          label="Market Price (₹)"
          value={price}
          min={50}
          max={1000000}
          step={10}
          onChange={setPrice}
        />
        <NumberField
          label="Coupon Rate (% p.a.)"
          value={coupon}
          min={0}
          max={20}
          step={0.1}
          onChange={setCoupon}
        />
        <Field
          label="Years to Maturity"
          valueLabel={String(yrs)}
          min={1}
          max={40}
          step={1}
          value={yrs}
          onChange={setYrs}
        />
      </div>
      <Results
        rows={[
          { label: "Approx. YTM", value: formatPct(ytm), hero: true },
          { label: "Current Yield", value: formatPct(cy) },
          { label: "Annual Coupon", value: formatINR(cpAmt) },
        ]}
      />
    </div>
  );
}

const PANELS = {
  sip: SipPanel,
  lumpsum: LumpsumPanel,
  retire: RetirementPanel,
  emi: EmiPanel,
  cagr: CagrPanel,
  goal: GoalPanel,
  bond: BondPanel,
};

export default function CalcTeaser() {
  const ref = useReveal();
  const [tab, setTab] = useState("sip");
  const Panel = PANELS[tab] || SipPanel;

  return (
    <section className="section" id="calculators-teaser">
      <div className="container" ref={ref}>
        <div className="home-section-head section-head pm-reveal">
          <span className="kicker">Free Tools</span>
          <h2>Run the numbers before you run the risk</h2>
          <p className="sub">
            Seven planning calculators with live results and Indian formatting.
          </p>
        </div>

        <div className="home-tabs" role="tablist" aria-label="Calculators">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`home-tab${tab === t.id ? " active" : ""}`}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="home-calc-wrap pm-reveal">
          <Panel />
          <div className="home-calc-footer">
            <p className="home-calc-note">
             Mutual fund investments
              are subject to market risks. Read all scheme-related documents carefully.
            </p>
            <Link to="/calculators" className="home-calc-more">
              Open full calculators →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
